// src/pages/PurchaseOrderDetailPage.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Paper,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Payments as PaymentsIcon,
  EmailOutlined as EmailIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import { purchaseOrderService } from '../services/purchaseOrderService';
import EditPOModal from '../components/PO/EditPOModal';


const PurchaseOrderDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const navigate = useNavigate();

  
  // State for PO data
  const [purchaseOrder, setPurchaseOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // State for invoice tracking
  const [recordInvoiceOpen, setRecordInvoiceOpen] = useState(false);
  const [selectedInvoiceIndex, setSelectedInvoiceIndex] = useState<number>(-1);
  const [invoiceData, setInvoiceData] = useState({
    invoiceNumber: '',
    invoiceDate: '',
    receiptDate: '',
    notes: ''
  });
  
  // State for payment tracking
  const [recordPaymentOpen, setRecordPaymentOpen] = useState(false);
  const [paymentData, setPaymentData] = useState({
    paymentDate: '',
    notes: ''
  });
  
  // State for status updates
  const [statusUpdateOpen, setStatusUpdateOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  
  // Load PO data
  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const po = await purchaseOrderService.getPurchaseOrderById(id);
        setPurchaseOrder(po);
        setError(null);
      } catch (error) {
        console.error('Error fetching purchase order:', error);
        setError('Failed to load purchase order details');
      } finally {
        setLoading(false);
      }
    };
    
    fetchPurchaseOrder();
  }, [id]);
  
  // Format date
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };
  
  // Format currency
  const formatCurrency = (amount: number) => {
    return `€${amount.toFixed(2)}`;
  };
  
  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created': return 'default';
      case 'approved': return 'primary';
      case 'partially_invoiced': return 'info';
      case 'fully_invoiced': return 'secondary';
      case 'partially_paid': return 'warning';
      case 'fully_paid': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };
  
  // Format status label
  const formatStatus = (status: string) => {
    return status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };
  
  // Handle recording an invoice
  const handleRecordInvoice = (index: number) => {
    setSelectedInvoiceIndex(index);
    setInvoiceData({
      invoiceNumber: '',
      invoiceDate: new Date().toISOString().split('T')[0],
      receiptDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setRecordInvoiceOpen(true);
  };
  
  // Submit invoice record
  const submitInvoiceRecord = async () => {
    if (!id || selectedInvoiceIndex < 0) return;
    
    try {
      const response = await purchaseOrderService.recordInvoice(id, {
        invoiceIndex: selectedInvoiceIndex,
        invoiceNumber: invoiceData.invoiceNumber,
        invoiceDate: new Date(invoiceData.invoiceDate),
        receiptDate: new Date(invoiceData.receiptDate),
        notes: invoiceData.notes
      });
      
      setPurchaseOrder(response);
      setRecordInvoiceOpen(false);
    } catch (error) {
      console.error('Error recording invoice:', error);
      setError('Failed to record invoice');
    }
  };
  
  // Handle recording a payment
  const handleRecordPayment = (index: number) => {
    setSelectedInvoiceIndex(index);
    setPaymentData({
      paymentDate: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setRecordPaymentOpen(true);
  };
  
  // Submit payment record
  const submitPaymentRecord = async () => {
    if (!id || selectedInvoiceIndex < 0) return;
    
    try {
      const response = await purchaseOrderService.recordPayment(id, {
        invoiceIndex: selectedInvoiceIndex,
        paymentDate: new Date(paymentData.paymentDate),
        notes: paymentData.notes
      });
      
      setPurchaseOrder(response);
      setRecordPaymentOpen(false);
    } catch (error) {
      console.error('Error recording payment:', error);
      setError('Failed to record payment');
    }
  };
  
  // Handle status update
  const handleStatusUpdate = () => {
    setNewStatus(purchaseOrder?.status || '');
    setStatusUpdateOpen(true);
  };
  
  // Submit status update
  const submitStatusUpdate = async () => {
    if (!id || !newStatus) return;
    
    try {
      const response = await purchaseOrderService.updatePurchaseOrderStatus(id, newStatus);
      setPurchaseOrder(response);
      setStatusUpdateOpen(false);
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };
  
  // Email to Monday.com
  const handleEmailToMonday = async () => {
    if (!id) return;
    
    try {
      await purchaseOrderService.emailPurchaseOrder(id);
      alert('Purchase order emailed to Monday.com successfully!');
    } catch (error) {
      console.error('Error emailing purchase order:', error);
      setError('Failed to email purchase order to Monday.com');
    }
  };
  
  if (loading) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }
  
  if (error) {
    return (
      <Container>
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/purchase-orders')}
          >
            Back to Purchase Orders
          </Button>
        </Box>
      </Container>
    );
  }
  
  if (!purchaseOrder) {
    return (
      <Container>
        <Box sx={{ py: 4 }}>
          <Alert severity="warning">
            Purchase order not found
          </Alert>
          <Button 
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/purchase-orders')}
          >
            Back to Purchase Orders
          </Button>
        </Box>
      </Container>
    );
  }
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate('/admin/purchase-orders')}
              sx={{ mr: 2 }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              Purchase Order: {purchaseOrder.poNumber}
            </Typography>
          </Box>
          
          <Chip
            label={formatStatus(purchaseOrder.status)}
            color={getStatusColor(purchaseOrder.status)}
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
        
        {/* Main content */}
        <Grid container spacing={3}>
          {/* Performance info */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Performance Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <List dense>
                <ListItem>
                  <ListItemText 
                    primary="Artist/Band" 
                    secondary={purchaseOrder.bandName}
                    primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'text.primary', variant: 'body1', fontWeight: 'bold' }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary="Event Date" 
                    secondary={formatDate(purchaseOrder.eventDate)}
                    primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'text.primary', variant: 'body1' }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary="Venue" 
                    secondary={purchaseOrder.venue || 'N/A'}
                    primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'text.primary', variant: 'body1' }}
                  />
                </ListItem>
                
                <ListItem>
                  <ListItemText 
                    primary="IMC Code" 
                    secondary={purchaseOrder.imcCode || 'N/A'}
                    primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'text.primary', variant: 'body1' }}
                  />
                </ListItem>
                
                {purchaseOrder.notes && (
                  <ListItem>
                    <ListItemText 
                      primary="Notes" 
                      secondary={purchaseOrder.notes}
                      primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                      secondaryTypographyProps={{ color: 'text.primary', variant: 'body1' }}
                    />
                  </ListItem>
                )}
                
                <ListItem>
                  <ListItemText 
                    primary="Created At" 
                    secondary={formatDate(purchaseOrder.createdAt)}
                    primaryTypographyProps={{ color: 'text.secondary', variant: 'body2' }}
                    secondaryTypographyProps={{ color: 'text.primary', variant: 'body1' }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
          
          {/* Financial Summary */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Financial Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Total Amount:
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(purchaseOrder.totalAmount)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Invoiced Amount:
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(purchaseOrder.invoicedAmount)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Paid Amount:
                    </Typography>
                    <Typography variant="h6">
                      {formatCurrency(purchaseOrder.paidAmount)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Outstanding:
                    </Typography>
                    <Typography variant="h6" color="error">
                      {formatCurrency(purchaseOrder.totalAmount - purchaseOrder.paidAmount)}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                          <Button
  variant="outlined"
  startIcon={<EditIcon />}
  onClick={() => setEditModalOpen(true)}
>
  Edit Details
</Button>
                <Button
                  variant="outlined"
                  startIcon={<PrintIcon />}
                  onClick={() => window.print()}
                >
                  Print PO
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  onClick={handleEmailToMonday}
                  color="primary"
                >
                  Email to Monday
                </Button>
                
                <Button
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleStatusUpdate}
                  color="secondary"
                >
                  Update Status
                </Button>
              </Box>
            </Paper>
          </Grid>
          
          {/* Expected Invoices Section */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Expected Invoices
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell>Payee</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>IMC Code</TableCell> {/* Add this column */}
                      <TableCell>Status</TableCell>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Received</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {purchaseOrder.expectedInvoices.map((invoice: any, index: number) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          '&:hover': { bgcolor: 'action.hover' },
                          bgcolor: invoice.status === 'paid' 
                            ? 'success.lighter' 
                            : invoice.status === 'received'
                              ? 'info.lighter'
                              : 'inherit'
                        }}
                      >
                        <TableCell>
                          {invoice.category.charAt(0).toUpperCase() + invoice.category.slice(1)}
                        </TableCell>
                        <TableCell>{invoice.payee}</TableCell>
                        <TableCell>{invoice.description || '-'}</TableCell>
                        <TableCell align="right">{formatCurrency(invoice.amount)}</TableCell>
                        <TableCell>{invoice.imcCode || '-'}</TableCell> {/* Add this cell */}

                        <TableCell>
                          <Chip
                            label={invoice.status}
                            color={
                              invoice.status === 'paid' 
                                ? 'success' 
                                : invoice.status === 'received'
                                  ? 'info'
                                  : 'default'
                            }
                            size="small"
                          />
                        </TableCell>
                        <TableCell>{invoice.invoiceNumber || '-'}</TableCell>
                        <TableCell>{invoice.receiptDate ? formatDate(invoice.receiptDate) : '-'}</TableCell>
                        <TableCell>
                          {invoice.status === 'pending' && (
                            <Button
                              size="small"
                              startIcon={<ReceiptIcon />}
                              onClick={() => handleRecordInvoice(index)}
                            >
                              Record Invoice
                            </Button>
                          )}
                          
                          {invoice.status === 'received' && (
                            <Button
                              size="small"
                              startIcon={<PaymentsIcon />}
                              onClick={() => handleRecordPayment(index)}
                              color="primary"
                            >
                              Record Payment
                            </Button>
                          )}
                          
                          {invoice.status === 'paid' && (
                            <Chip
                              icon={<CheckCircleIcon />}
                              label="Paid"
                              color="success"
                              size="small"
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
        
        {/* Record Invoice Dialog */}
        <Dialog
          open={recordInvoiceOpen}
          onClose={() => setRecordInvoiceOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Record Invoice</DialogTitle>
          <DialogContent>
            {selectedInvoiceIndex >= 0 && purchaseOrder.expectedInvoices[selectedInvoiceIndex] && (
              <Box sx={{ pt: 2 }}>
                <Typography gutterBottom>
                  Recording invoice for: <strong>{purchaseOrder.expectedInvoices[selectedInvoiceIndex].payee}</strong>
                </Typography>
                <Typography gutterBottom>
                  Amount: <strong>{formatCurrency(purchaseOrder.expectedInvoices[selectedInvoiceIndex].amount)}</strong>
                </Typography>
                
                <TextField
                  fullWidth
                  label="Invoice Number"
                  value={invoiceData.invoiceNumber}
                  onChange={(e) => setInvoiceData({ ...invoiceData, invoiceNumber: e.target.value })}
                  margin="normal"
                  required
                />
                
                <TextField
                  fullWidth
                  label="Invoice Date"
                  type="date"
                  value={invoiceData.invoiceDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, invoiceDate: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
                
                <TextField
                  fullWidth
                  label="Receipt Date"
                  type="date"
                  value={invoiceData.receiptDate}
                  onChange={(e) => setInvoiceData({ ...invoiceData, receiptDate: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
                
                <TextField
                  fullWidth
                  label="Notes"
                  value={invoiceData.notes}
                  onChange={(e) => setInvoiceData({ ...invoiceData, notes: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecordInvoiceOpen(false)} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={submitInvoiceRecord} 
              color="primary" 
              variant="contained"
              disabled={!invoiceData.invoiceNumber}
            >
              Record Invoice
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Record Payment Dialog */}
        <Dialog
          open={recordPaymentOpen}
          onClose={() => setRecordPaymentOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Record Payment</DialogTitle>
          <DialogContent>
            {selectedInvoiceIndex >= 0 && purchaseOrder.expectedInvoices[selectedInvoiceIndex] && (
              <Box sx={{ pt: 2 }}>
                <Typography gutterBottom>
                  Recording payment for invoice: <strong>{purchaseOrder.expectedInvoices[selectedInvoiceIndex].invoiceNumber}</strong>
                </Typography>
                <Typography gutterBottom>
                  Payee: <strong>{purchaseOrder.expectedInvoices[selectedInvoiceIndex].payee}</strong>
                </Typography>
                <Typography gutterBottom>
                  Amount: <strong>{formatCurrency(purchaseOrder.expectedInvoices[selectedInvoiceIndex].amount)}</strong>
                </Typography>
                
                <TextField
                  fullWidth
                  label="Payment Date"
                  type="date"
                  value={paymentData.paymentDate}
                  onChange={(e) => setPaymentData({ ...paymentData, paymentDate: e.target.value })}
                  margin="normal"
                  InputLabelProps={{ shrink: true }}
                />
                
                <TextField
                  fullWidth
                  label="Notes"
                  value={paymentData.notes}
                  onChange={(e) => setPaymentData({ ...paymentData, notes: e.target.value })}
                  margin="normal"
                  multiline
                  rows={3}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setRecordPaymentOpen(false)} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={submitPaymentRecord} 
              color="primary" 
              variant="contained"
            >
              Record Payment
            </Button>
          </DialogActions>
        </Dialog>
        
        {/* Status Update Dialog */}
        <Dialog
          open={statusUpdateOpen}
          onClose={() => setStatusUpdateOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Update Purchase Order Status</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2 }}>
              <TextField
                select
                fullWidth
                label="Status"
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                margin="normal"
              >
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="partially_invoiced">Partially Invoiced</MenuItem>
                <MenuItem value="fully_invoiced">Fully Invoiced</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
                <MenuItem value="fully_paid">Fully Paid</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setStatusUpdateOpen(false)} color="primary">
              Cancel
            </Button>
            <Button 
              onClick={submitStatusUpdate} 
              color="primary" 
              variant="contained"
            >
              Update Status
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
<EditPOModal
  open={editModalOpen}
  onClose={() => setEditModalOpen(false)}
  purchaseOrder={purchaseOrder}
  onSave={async (updatedData) => {
    if (!id) {
      setError('Purchase order ID is missing');
      return;
    }
    
    try {
      const response = await purchaseOrderService.updatePurchaseOrder(id, updatedData);
      setPurchaseOrder(response);
      setEditModalOpen(false);
    } catch (error) {
      console.error('Error updating purchase order:', error);
      setError('Failed to update purchase order');
    }
  }}
/>
    </Container>
    
  );
};

export default PurchaseOrderDetailPage;